import React, { useState, useEffect } from 'react';
import useBot from '../../hooks/useBot';
import SalesConversations from './SalesConversations';
import {
  MessageSquare,
  RefreshCw,
  Search,
  Clock,
  User,
  Phone,
  Calendar,
  Eye,
  PauseCircle,
  PlayCircle,
  X as CloseIcon,
  AlertCircle,
  RotateCcw,
} from 'lucide-react';
import { Card, Loading, Badge } from '../../components/common';
import ResetConfirmationModal from '../../components/dashboard/ResetConfirmationModal/ResetConfirmationModal';
import conversationsService from '../../services/api/conversations.service';
import adminConversationsService from '../../services/api/admin.conversations.service';
import tenantsService from '../../services/api/tenants.service';
import useAuth from '../../hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Função helper para formatar datas com segurança
const formatSafeDate = (dateString) => {
  try {
    if (!dateString) return 'Data não disponível';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Data inválida';
    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: ptBR,
    });
  } catch (error) {
    console.error('Erro ao formatar data:', dateString, error);
    return 'Data inválida';
  }
};

const SACConversations = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.type === 'admin';
  
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState(null);
  const [conversationToReset, setConversationToReset] = useState(null);

  // Carregar tenants para admin
  useEffect(() => {
    if (isAdmin) {
      loadTenants();
    } else {
      loadConversations();
    }
  }, [isAdmin]);

  // Carregar conversas quando tenant mudar (não quando filtro mudar)
  useEffect(() => {
    if (selectedTenantId || !isAdmin) {
      loadConversations();
    }
  }, [selectedTenantId]); // ⚠️ REMOVIDO statusFilter daqui

  const loadTenants = async () => {
    try {
      const data = await tenantsService.getAll();
      setTenants(data);
      if (data.length > 0) {
        setSelectedTenantId(data[0].id);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar tenants:', error);
      setError('Erro ao carregar tenants: ' + error.message);
      setLoading(false);
    }
  };

  const loadConversations = async (reset = true) => {
    try {
      setLoading(true);
      setError(null);

      let tenantId;
      let needsAdminToken = false;
      
      if (isAdmin) {
        if (!selectedTenantId) {
          setConversations([]);
          setLoading(false);
          return;
        }
        tenantId = selectedTenantId;
        needsAdminToken = true;
      } else {
        tenantId = user?.tenantId || user?.id;
      }

      console.log('📊 Carregando conversas para tenant:', tenantId);

      if (needsAdminToken) {
        console.log('🔑 Admin detectado - gerando token temporário do tenant...');
        try {
          const tokenData = await tenantsService.generateToken(tenantId);
          localStorage.setItem('temp_tenant_token', tokenData.token);
          console.log('✅ Token temporário gerado');
        } catch (tokenError) {
          console.error('❌ Erro ao gerar token do tenant:', tokenError);
          throw new Error('Não foi possível autenticar como tenant');
        }
      }

      const conversationsList = await conversationsService.getAllConversations(tenantId, {
        status: 'all',
      });

      if (needsAdminToken) {
        localStorage.removeItem('temp_tenant_token');
      }

      console.log('✅ Conversas carregadas:', conversationsList.length);

      setConversations(conversationsList);
      setHasMore(false);
      setNextCursor(null);

    } catch (error) {
      console.error('❌ Erro ao carregar conversas:', error);
      setError(error.message || 'Erro ao carregar conversas');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTenantChange = (tenantId) => {
    console.log('🔄 Mudando tenant para:', tenantId);
    setSelectedTenantId(tenantId);
    setConversations([]);
    setNextCursor(null);
    setHasMore(false);
  };

  const handleViewMessages = async (conversation) => {
    try {
      setLoadingMessages(true);
      setSelectedConversation(conversation);
      setMessages([]);
      
      let tenantId;
      let needsAdminToken = false;
      
      if (isAdmin) {
        tenantId = selectedTenantId;
        needsAdminToken = true;
      } else {
        tenantId = user?.tenantId || user?.id;
      }

      console.log('📨 Buscando mensagens para conversa:', conversation.id);

      if (needsAdminToken) {
        const tokenData = await tenantsService.generateToken(tenantId);
        localStorage.setItem('temp_tenant_token', tokenData.token);
      }

      const data = await conversationsService.getMessages(tenantId, conversation.id);
      
      if (needsAdminToken) {
        localStorage.removeItem('temp_tenant_token');
      }

      const messagesList = data.items || data.messages || [];
      
      setMessages(messagesList);
      console.log('✅ Mensagens carregadas:', messagesList.length);
      
    } catch (error) {
      console.error('❌ Erro ao carregar mensagens:', error);
      
      localStorage.removeItem('temp_tenant_token');
      
      setMessages([]);
      
      let errorMessage = 'Erro ao carregar mensagens';
      
      if (error.message === 'Network Error') {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Tempo de espera excedido. Tente novamente.';
      } else if (error.message) {
        errorMessage = `Erro: ${error.message}`;
      }
      
      alert(errorMessage);
      
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleCloseConversation = async (conversation, e) => {
    if (e) e.stopPropagation();
    
    const reason = prompt('Digite o motivo do fechamento:');
    if (!reason) return;

    try {
      let tenantId;
      let needsAdminToken = false;
      
      if (isAdmin) {
        tenantId = selectedTenantId;
        needsAdminToken = true;
      } else {
        tenantId = user?.tenantId || user?.id;
      }

      if (needsAdminToken) {
        const tokenData = await tenantsService.generateToken(tenantId);
        localStorage.setItem('temp_tenant_token', tokenData.token);
      }

      await conversationsService.closeConversation(tenantId, conversation.id, { reason });
      
      if (needsAdminToken) {
        localStorage.removeItem('temp_tenant_token');
      }
      
      alert('Conversa fechada com sucesso!');
      await loadConversations();
      setSelectedConversation(null);
      
    } catch (error) {
      console.error('❌ Erro ao fechar conversa:', error);
      alert('Erro ao fechar conversa: ' + error.message);
    }
  };

  const handlePauseBot = async (conversation, e) => {
    if (e) e.stopPropagation();
    
    try {
      let tenantId;
      
      if (isAdmin) {
        tenantId = selectedTenantId;
      } else {
        tenantId = user?.tenantId || user?.id;
      }

      const waId = conversation.wa_id || conversation.lead?.phone;
      
      if (!waId) {
        alert('Número do WhatsApp não encontrado');
        return;
      }

      await conversationsService.pauseBot(waId, tenantId);
      
      alert('Bot pausado com sucesso!');
      await loadConversations();
      
    } catch (error) {
      console.error('❌ Erro ao pausar bot:', error);
      alert('Erro ao pausar bot: ' + error.message);
    }
  };

  const handleResumeBot = async (conversation, e) => {
    if (e) e.stopPropagation();
    
    try {
      let tenantId;
      
      if (isAdmin) {
        tenantId = selectedTenantId;
      } else {
        tenantId = user?.tenantId || user?.id;
      }

      const waId = conversation.wa_id || conversation.lead?.phone;
      
      if (!waId) {
        alert('Número do WhatsApp não encontrado');
        return;
      }

      await conversationsService.resumeBot(waId, tenantId);
      
      alert('Bot retomado com sucesso!');
      await loadConversations();
      
    } catch (error) {
      console.error('❌ Erro ao retomar bot:', error);
      alert('Erro ao retomar bot: ' + error.message);
    }
  };

  const handleResetConversation = async (conversation, e) => {
    if (e) e.stopPropagation();
    setConversationToReset(conversation);
  };

  const confirmResetConversation = async () => {
    if (!conversationToReset) return;

    try {
      console.log('🔄 Admin resetando conversa:', conversationToReset.id);

      await adminConversationsService.resetConversation(conversationToReset.id);
      
      alert('✅ Conversa resetada com sucesso!\n\nO fluxo foi reiniciado e o cliente receberá a mensagem inicial.');
      await loadConversations();
      
    } catch (error) {
      console.error('❌ Erro ao resetar conversa:', error);
      alert('Erro ao resetar conversa: ' + error.message);
    }
  };

  const getClientName = (conversation) => {
    return conversation.context?.nome || 
           conversation.context?.name ||
           conversation.lead?.name || 
           conversation.profile_name || 
           conversation.clientName ||
           'Cliente';
  };

  const getClientPhone = (conversation) => {
    return conversation.lead?.phone || 
           conversation.wa_id || 
           conversation.phone ||
           '';
  };

  const getConversationStatus = (conversation) => {
    let currentStep = '';
    
    if (conversation.context) {
      currentStep = conversation.context.current_step || 
                   conversation.context.currentStep || 
                   conversation.context.step ||
                   conversation.context.stepId ||
                   conversation.context.step_id || '';
    }
    
    if (!currentStep && conversation) {
      currentStep = conversation.current_step ||
                   conversation.currentStep ||
                   conversation.step || '';
    }
    
    const stepStr = String(currentStep).toLowerCase();
    
    if (stepStr.includes('perguntar_reinicio')) {
      return 'closed';
    }
    
    if (conversation.context?.flow_finished === true || 
        conversation.context?.flow_finished === 'true') {
      return 'closed';
    }
    
    if (conversation.context?.restarted_by_user === true || 
        conversation.context?.restarted_by_user === 'true') {
      return 'open';
    }
    
    return conversation.status || 'open';
  };

  const isAwaitingRestart = (conversation) => {
    let currentStep = '';
    
    if (conversation.context) {
      currentStep = conversation.context.current_step || 
                   conversation.context.currentStep || 
                   conversation.context.step ||
                   conversation.context.stepId ||
                   conversation.context.step_id || '';
    }
    
    if (!currentStep && conversation) {
      currentStep = conversation.current_step ||
                   conversation.currentStep ||
                   conversation.step || '';
    }
    
    const stepStr = String(currentStep).toLowerCase();
    
    if (stepStr.includes('perguntar_reinicio')) {
      return true;
    }
    
    return conversation.context?.awaiting_restart_response === true || 
           conversation.context?.awaiting_restart_response === 'true';
  };

  const filteredConversations = conversations.filter((conv) => {
    // Primeiro filtra por status usando nossa função
    const currentStatus = getConversationStatus(conv);
    
    // Aplica filtro de status
    if (statusFilter === 'open' && currentStatus !== 'open') {
      return false;
    }
    if (statusFilter === 'closed' && currentStatus !== 'closed') {
      return false;
    }
    // statusFilter === 'all' não precisa filtrar
    
    // Depois filtra por busca
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    const name = getClientName(conv).toLowerCase();
    const phone = getClientPhone(conv);
    const id = conv.id?.toLowerCase() || '';
    const protocol = conv.protocol?.toLowerCase() || '';
    
    return name.includes(search) || 
           phone.includes(search) || 
           id.includes(search) ||
           protocol.includes(search);
  });

  if (loading && conversations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" text="Carregando conversas..." />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2.5 font-display">
            <div className="w-8 h-8 bg-primary-50 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            </div>
            Conversas
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 ml-10">
            {isAdmin
              ? 'Visualização Admin — gerencie conversas de todos os tenants'
              : 'Gerencie e acompanhe suas conversas com clientes'}
          </p>
        </div>

        <button
          onClick={() => loadConversations(true)}
          disabled={loading}
          className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed self-start sm:self-auto"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {isAdmin && tenants.length > 0 && (
        <Card padding="sm">
          <Card.Body className="p-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 flex-shrink-0">
                Tenant:
              </label>
              <select
                value={selectedTenantId || ''}
                onChange={(e) => handleTenantChange(e.target.value)}
                className="flex-1 px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-300 focus:ring-2 focus:ring-primary-400/30 focus:outline-none"
              >
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} ({tenant.id})
                  </option>
                ))}
              </select>
            </div>
          </Card.Body>
        </Card>
      )}

      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700 dark:text-red-300 mb-0.5">Erro ao carregar conversas</p>
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={() => loadConversations(true)}
              className="mt-2 text-xs font-semibold text-red-600 dark:text-red-400 hover:underline"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      )}

      <Card padding="sm">
        <Card.Body className="p-3 space-y-2.5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nome, telefone, protocolo ou ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-300 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400 focus:bg-white dark:focus:bg-slate-800 focus:outline-none transition-colors"
            />
          </div>

          <div className="flex gap-1.5">
            {[
              { key: 'open',   label: 'Abertas' },
              { key: 'closed', label: 'Fechadas' },
              { key: 'all',    label: 'Todas' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                  statusFilter === key
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </Card.Body>
      </Card>

      {!selectedTenantId && isAdmin ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
            <MessageSquare className="w-6 h-6 text-slate-300 dark:text-slate-600" />
          </div>
          <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">Selecione um Tenant</h3>
          <p className="text-sm text-slate-400 dark:text-slate-500">Escolha um tenant acima para visualizar suas conversas</p>
        </div>
      ) : filteredConversations.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
            <MessageSquare className="w-6 h-6 text-slate-300 dark:text-slate-600" />
          </div>
          <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">Nenhuma conversa encontrada</h3>
          <p className="text-sm text-slate-400 dark:text-slate-500">
            {searchTerm
              ? 'Tente ajustar os filtros de busca'
              : statusFilter === 'open'
              ? 'Não há conversas abertas no momento'
              : statusFilter === 'closed'
              ? 'Não há conversas fechadas'
              : 'Não há conversas registradas'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredConversations.map((conversation) => {
            const isOpen = getConversationStatus(conversation) === 'open';
            return (
              <div
                key={conversation.id}
                className="rounded-xl border border-slate-200 dark:border-slate-700/60 bg-white dark:bg-slate-800/70 shadow-sm overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-primary-50 dark:bg-primary-900/25 rounded-xl flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                          {getClientName(conversation)}
                        </h3>
                        <Badge variant={isOpen ? 'success' : 'default'} size="sm">
                          {isOpen ? 'Aberta' : 'Fechada'}
                        </Badge>
                        {conversation.bot_paused && <Badge variant="warning" size="sm">Bot Pausado</Badge>}
                        {isAwaitingRestart(conversation) && <Badge variant="primary" size="sm">Aguardando Reinício</Badge>}
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                          <Phone className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate max-w-[180px]">{getClientPhone(conversation)}</span>
                        </div>
                        {conversation.protocol && (
                          <div className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 font-mono">
                            <span>#{conversation.protocol}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
                          <Calendar className="w-3 h-3 flex-shrink-0" />
                          <span>Iniciada {formatSafeDate(conversation.created_at || conversation.createdAt)}</span>
                        </div>
                        {(conversation.updated_at || conversation.lastInboundAt) && (
                          <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
                            <Clock className="w-3 h-3 flex-shrink-0" />
                            <span>Atualizada {formatSafeDate(conversation.updated_at || conversation.lastInboundAt)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                    <button
                      onClick={() => handleViewMessages(conversation)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Ver
                    </button>

                    {isOpen && (
                      <>
                        {conversation.bot_paused ? (
                          <button
                            onClick={(e) => handleResumeBot(conversation, e)}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                          >
                            <PlayCircle className="w-3.5 h-3.5" />
                            Retomar
                          </button>
                        ) : (
                          <button
                            onClick={(e) => handlePauseBot(conversation, e)}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                          >
                            <PauseCircle className="w-3.5 h-3.5" />
                            Pausar
                          </button>
                        )}
                        <button
                          onClick={(e) => handleCloseConversation(conversation, e)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                        >
                          <CloseIcon className="w-3.5 h-3.5" />
                          Fechar
                        </button>
                      </>
                    )}

                    {isAdmin && (
                      <button
                        onClick={(e) => handleResetConversation(conversation, e)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800/40 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                        title="Resetar conversa e reiniciar fluxo"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Resetar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedConversation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700">

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 bg-primary-50 dark:bg-primary-900/25 rounded-xl flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
                    {getClientName(selectedConversation)}
                  </p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 truncate">
                    {getClientPhone(selectedConversation)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedConversation(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0 ml-2"
              >
                <CloseIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-5 custom-scrollbar bg-slate-50 dark:bg-slate-950">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-12">
                  <Loading size="md" text="Carregando mensagens..." />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-sm text-slate-400 dark:text-slate-500">Nenhuma mensagem encontrada</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((message, index) => {
                    const dir = message.direction || '';
                    const isOutbound =
                      dir === 'outbound' || dir === 'bot' || dir === 'agent' ||
                      dir === 'human' || dir === 'out' ||
                      message.fromMe === true || message.from_me === true;

                    let msgText = '[Mensagem sem texto]';
                    if (message.text && typeof message.text === 'string') {
                      msgText = message.text;
                    } else if (message.body) {
                      if (typeof message.body === 'string') {
                        msgText = message.body;
                      } else if (message.body.text) {
                        msgText = message.body.text;
                      } else if (typeof message.body === 'object') {
                        const typeMap = { image: '📷 Imagem', video: '🎥 Vídeo', audio: '🎵 Áudio', document: '📄 Documento' };
                        msgText = typeMap[message.body.type] || `[${message.body.type || 'desconhecido'}]`;
                      }
                    }

                    return (
                      <div key={message.id || index} className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[72%] rounded-2xl px-4 py-2.5 ${
                          isOutbound
                            ? 'bg-primary-500 text-white rounded-br-sm'
                            : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-bl-sm shadow-sm'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{msgText}</p>
                          <p className={`text-[11px] mt-1.5 ${isOutbound ? 'text-white/60' : 'text-slate-400 dark:text-slate-500'}`}>
                            {new Date(message.timestamp || message.createdAt || message.created_at).toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {conversationToReset && (
        <ResetConfirmationModal
          conversation={conversationToReset}
          onConfirm={confirmResetConversation}
          onClose={() => setConversationToReset(null)}
        />
      )}
    </div>
  );
};

const Conversations = () => {
  const { isSalesBot } = useBot();
  return isSalesBot ? <SalesConversations /> : <SACConversations />;
};

export default Conversations;